import React, { FC, PropsWithChildren, useState } from 'react';
import styled, { css } from 'styled-components';
import { getFluidGatsbyImage } from '../../utils/gatsby-storyblok-image-fixed';
import LazyImage from '../lazy-image';
import Link from '../link';
import { Grid, Paragraph } from '../UIKit/system';
import fallbackImage from '../../images/tmp/modal-shortcut-background2.jpg';
import DownloadPopup from '../resource-centre/download-popup';
import { fontFamilySans, getThemeColor } from '../../utils/styles';
import mq from '../../utils/mq';

export interface SearchCardProps {
  id: number;
  title: string;
  description: string;
  slug?: string | null;
  image?: string;
  download?: string;
}

export const SearchCard: FC<SearchCardProps> = (props) => {
  const { title, description, slug, image, download } = props;

  return (
    <Wrapper slug={slug} download={download}>
      <Grid
        gridTemplateColumns={['auto 1fr', 'auto 1fr', 'auto 1fr auto']}
        gridColumnGap={20}
      >
        <CardImage
          fluid={
            image
              ? getFluidGatsbyImage(
                  { filename: image },
                  {
                    maxWidth: 300,
                    maxHeight: 300
                  }
                )
              : undefined
          }
          alt={title}
          fallback={!image}
        />

        <div>
          <Title>{title}</Title>
          <Description>{description}</Description>
        </div>
        <Grid>
          {slug && <ActionText>Read Now</ActionText>}
          {download && <ActionText>Download</ActionText>}
        </Grid>
      </Grid>
    </Wrapper>
  );
};

/**
 * Wrap with a link when slug has a value,
 * otherwise wrap in a div element.
 */
const Wrapper: FC<
  PropsWithChildren<{ slug?: string | null; download?: string }>
> = ({ slug, download, children }) => {
  if (!slug && !download) return <div>{children}</div>;

  if (download) {
    return <DownloadWrap download={download}>{children}</DownloadWrap>;
  }

  return (
    <Link to={`/${slug}`} target="_blank">
      {children}
    </Link>
  );
};

const CardImage = styled(LazyImage).attrs((props: { fallback?: boolean }) => ({
  url: props.fallback ? fallbackImage : undefined,
  height: 75,
  width: 75
}))`
  background-color: ${getThemeColor('lightGrey')};
  border-radius: 8px;

  ${mq.tablet(css`
    background-color: ${getThemeColor('white')};
  `)}
`;

const Title = styled(Paragraph).attrs(() => ({
  color: ['white', 'white', 'black'],
  fontSize: [14, 14, 16],
  fontWeight: 'bold',
  mb: 4,
  textAlign: 'left'
}))`
  a:hover &,
  a:active &,
  a:focus &,
  button:hover &,
  button:focus &,
  button:active & {
    color: ${getThemeColor('vividPrimary')};
  }
`;

const Description = styled(Paragraph).attrs(() => ({
  color: ['grey4', 'lightGrey', 'grey'],
  fontSize: 14,
  textAlign: 'left'
}))``;

const ActionText = styled(Paragraph).attrs(() => ({
  alignSelf: 'center',
  color: 'vividPrimary',
  display: ['none', 'none', 'block'],
  fontSize: 14,
  fontWeight: 'bold',
  margin: 0,
  padding: 0
}))``;

const DownloadWrap: FC<PropsWithChildren<{ download: string }>> = ({
  download,
  children
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <DownloadButtonWrap type="button" onClick={() => setOpen(true)}>
        {children}
      </DownloadButtonWrap>

      {open && (
        <DownloadPopup
          filePath={download}
          handlePopupClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

const DownloadButtonWrap = styled.button`
  ${fontFamilySans}
  background: none;
  border: none;
  cursor: pointer;
`;
